"use client";
import { useResettableState } from "@/src/hooks/useResettableState";

import * as React from "react";
import Image from "next/image";
import { ThumbsUp, ThumbsDown, MessageCircleMore, Send, Loader2, ChevronDown } from "lucide-react";

import { useApi } from "@/src/api/useApi";
import { handleErrors, post } from "@/src/api/apiHandler";
import { showToast } from "@/src/components/toast/showToast";
import { getTimeAgo, getUrlImage } from "@/src/tools";
import useUserStore from "@/src/store/useUser";
import useLoginStore from "@/src/store/useLoginStore";

type ApiUser = {
  id: number;
  user_first_name?: string | null;
  user_last_name?: string | null;
  user_avatar?: string | null;
};

type Comment = {
  id: number;
  comment_first_name?: string | null;
  comment_last_name?: string | null;
  comment_description: string;
  createdAt: string;
  comment_like?: number | null;
  comment_dislike?: number | null;
  User?: ApiUser | null;
  children?: Comment[];
};

type CommentListResponse = {
  data?: {
    rows?: Comment[];
    count?: number;
    totalPages?: number;
  };
};

interface Props {
  articleId: number;
}

const PAGE_SIZE = 8;

function fullName(c: Comment) {
  const fromUser = [c.User?.user_first_name, c.User?.user_last_name]
    .filter(Boolean)
    .join(" ");
  const fromComment = [c.comment_first_name, c.comment_last_name]
    .filter(Boolean)
    .join(" ");
  return fromUser || fromComment || "کاربر";
}

function getInitial(name: string) {
  return (name || "ک").trim().charAt(0);
}

function CommentCard({
  comment,
  depth = 0,
  onReply,
}: {
  comment: Comment;
  depth?: number;
  onReply: (parentId: number) => void;
}) {
  const [expanded, setExpanded] = React.useState(false);
  const name = fullName(comment);
  const avatarSrc = comment.User?.user_avatar
    ? getUrlImage(comment.User.user_avatar)
    : null;
  const children = comment.children ?? [];
  const hasReplies = children.length > 0;

  return (
    <div
      className={`rounded-xl border border-gray-100 bg-white p-3 sm:p-4 ${
        depth > 0 ? "mt-2 mr-6 sm:mr-10" : ""
      }`}
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 h-9 w-9 rounded-full bg-blue-50 text-blue-600 border border-blue-100 flex items-center justify-center overflow-hidden">
          {avatarSrc ? (
            <Image
              src={avatarSrc}
              alt={name}
              width={36}
              height={36}
              className="h-9 w-9 object-cover"
            />
          ) : (
            <span className="text-xs font-bold">{getInitial(name)}</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span className="font-semibold text-gray-800 text-[13px]">
              {name}
            </span>
            <span className="text-[11px]">{getTimeAgo(comment.createdAt)}</span>
          </div>

          <p className="mt-1.5 text-[13px] leading-7 text-gray-700 whitespace-pre-line break-words">
            {comment.comment_description}
          </p>

          <div className="mt-2 flex items-center gap-4 text-[11px] text-gray-500">
            <button
              type="button"
              className="inline-flex items-center gap-1 hover:text-blue-600 transition-colors"
              title="پسندیدم"
            >
              <ThumbsUp className="w-3.5 h-3.5" />
              <span>{comment.comment_like ?? 0}</span>
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-1 hover:text-rose-600 transition-colors"
              title="نپسندیدم"
            >
              <ThumbsDown className="w-3.5 h-3.5" />
              <span>{comment.comment_dislike ?? 0}</span>
            </button>
            <button
              type="button"
              onClick={() => onReply(comment.id)}
              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 mr-auto"
            >
              <MessageCircleMore className="w-3.5 h-3.5" />
              نوشتن جواب
            </button>
          </div>

          {hasReplies && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-2 inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-blue-600 transition-colors"
            >
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform ${
                  expanded ? "rotate-180" : ""
                }`}
              />
              {expanded ? "بستن جواب‌ها" : `مشاهده ${children.length} جواب`}
            </button>
          )}
        </div>
      </div>

      {expanded && hasReplies && (
        <div className="mt-2">
          {children.map((child) => (
            <CommentCard
              key={child.id}
              comment={child}
              depth={depth + 1}
              onReply={onReply}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const ArticleCommentsSection: React.FC<Props> = ({ articleId }) => {
  const { dataUser } = useUserStore();
  const { openLogin } = useLoginStore();

  const [page, setPage] = React.useState(1);
  const [formOpen, setFormOpen] = React.useState(false);
  const [parentId, setParentId] = React.useState<number | null>(null);
  const [firstName, setFirstName] = useResettableState(
    dataUser?.firstName || "",
  );
  const [lastName, setLastName] = useResettableState(dataUser?.lastName || "");
  const [description, setDescription] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  const { data, isLoading, refresh } = useApi<CommentListResponse>(
    `/comment-list?type=article&prod=${articleId}&page=${page}&size=${PAGE_SIZE}&sort=new`,
  );

  const rows = data?.data?.rows ?? [];
  const totalPages = data?.data?.totalPages ?? 1;
  const hasMore = page < totalPages;

  const openReply = (pid: number) => {
    if (!dataUser?.token) {
      openLogin();
      return;
    }
    setParentId(pid);
    setFormOpen(true);
    setTimeout(() => {
      document
        .getElementById("article-comment-form")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  const openNew = () => {
    if (!dataUser?.token) {
      openLogin();
      return;
    }
    setParentId(null);
    setFormOpen(true);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim() || description.trim().length < 3) {
      showToast("متن دیدگاه حداقل ۳ کاراکتر باشد", "error");
      return;
    }
    try {
      setSubmitting(true);
      await post("/comments", {
        comment_first_name: firstName.trim(),
        comment_last_name: lastName.trim(),
        comment_type: "article",
        comment_description: description.trim(),
        comment_parent: parentId,
        comment_rate: 0,
        Images: [],
        ProductId: null,
        ProductIdentityId: null,
        ArticleId: articleId,
      });
      showToast(
        "دیدگاه شما با موفقیت ثبت شد و پس از تأیید نمایش داده می‌شود.",
        "success",
      );
      setDescription("");
      setParentId(null);
      setFormOpen(false);
      refresh();
    } catch (e) {
      handleErrors(e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="comments"
      className="mt-8 rounded-2xl border border-gray-100 bg-white p-3 sm:p-5"
    >
      {/* عنوان + دکمه ثبت دیدگاه */}
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="text-base sm:text-lg font-bold text-gray-800 flex items-center gap-2">
          <span className="block w-1 h-5 rounded-full bg-blue-500"></span>
          دیدگاه‌ها
        </h2>
        <button
          type="button"
          onClick={openNew}
          className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold shadow-sm transition-colors"
        >
          <Send className="w-3.5 h-3.5" />
          ثبت دیدگاه
        </button>
      </div>

      {/* فرم ثبت دیدگاه (شرطی) */}
      {formOpen && (
        <form
          id="article-comment-form"
          onSubmit={submit}
          className="mb-4 rounded-xl border border-blue-100 bg-blue-50/30 p-3 space-y-2"
        >
          {parentId !== null && (
            <div className="text-[11px] text-blue-700">
              در حال پاسخ به دیدگاه #{parentId} —{" "}
              <button
                type="button"
                onClick={() => setParentId(null)}
                className="underline"
              >
                لغو پاسخ
              </button>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="نام"
              className="h-9 rounded-lg border border-gray-200 bg-white px-3 text-[12px] focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="نام خانوادگی"
              className="h-9 rounded-lg border border-gray-200 bg-white px-3 text-[12px] focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="دیدگاه شما..."
            rows={3}
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-[12px] leading-7 focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setFormOpen(false);
                setParentId(null);
              }}
              className="h-8 px-3 rounded-lg border border-gray-200 text-[11px] text-gray-600 hover:bg-gray-50"
            >
              انصراف
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-semibold disabled:opacity-60"
            >
              {submitting ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Send className="w-3.5 h-3.5" />
              )}
              ارسال
            </button>
          </div>
        </form>
      )}

      {/* لیست دیدگاه‌ها */}
      {isLoading && rows.length === 0 ? (
        <div className="py-6 flex items-center justify-center text-sm text-gray-400">
          <Loader2 className="w-4 h-4 animate-spin ml-2" />
          در حال بارگذاری…
        </div>
      ) : rows.length === 0 ? (
        <div className="py-8 text-center text-sm text-gray-400">
          هنوز دیدگاهی ثبت نشده است. اولین نفر باشید!
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((c) => (
            <CommentCard key={c.id} comment={c} onReply={openReply} />
          ))}
        </div>
      )}

      {/* بیشتر */}
      {hasMore && (
        <div className="mt-3 flex items-center justify-center">
          <button
            type="button"
            onClick={() => setPage((p) => p + 1)}
            className="inline-flex items-center gap-2 h-9 px-4 rounded-lg border border-blue-200 text-blue-600 text-xs font-semibold hover:bg-blue-50 transition-colors"
          >
            مشاهده بیشتر
          </button>
        </div>
      )}
    </section>
  );
};

export default ArticleCommentsSection;
